import { useEffect, useState } from "react";
import { Coffee, Layers, Timer, Trophy, Users } from "lucide-react";
import { toast } from "sonner";
import { api, C, errorMessage, fmtChips } from "../../lib/api";
import { shortGame } from "../../lib/gameName";

const mmss = (ms) => { const s = Math.max(0, Math.ceil(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
const ordinal = (n) => `${n}${n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th"}`;

// Tournament strip at an MTT table: level clock, field, prize pool, your table, plus re-buy / add-on when they are open.
export const MttBanner = ({ t, serverNow, myUserId, onRefresh }) => {
  const [, tick] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { const i = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(i); }, []);
  const now = serverNow();
  const me = t.places?.find((p) => p.user_id === myUserId);
  const paid = t.paid_places || [];
  const myPrize = me ? paid.find((p) => p.place === me.place)?.amount || 0 : 0;

  const buy = async (route, ok) => {
    setBusy(true);
    try { await api.post(route.replace("{event_id}", t.event_id)); toast.success(ok); onRefresh?.(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  const Pill = ({ children, testId, tone = "text-amber-100" }) => (
    <span data-testid={testId} className={`glass rounded-full px-3 py-1 text-xs flex items-center gap-1.5 tabular-nums whitespace-nowrap ${tone}`}>{children}</span>
  );
  return (
    <div data-testid="mtt-banner" data-status={t.status} className="flex flex-col items-center gap-1.5 px-6 -mt-14 pb-0 z-10 pointer-events-none [&>*]:pointer-events-auto">
      <div className="flex items-center gap-1.5 flex-wrap justify-center">
        {t.rotation?.length > 1 && (
          <Pill testId="mtt-rotation"><Layers className="w-3.5 h-3.5 text-amber-300" /> {shortGame(t.game)} · next {shortGame(t.next_game)}</Pill>
        )}
        <Pill testId="mtt-level">
          <Timer className="w-3.5 h-3.5 text-emerald-300" /> Level {t.level} · {t.sb}/{t.bb}
          {t.break_until || t.break_pending ? "" : t.next_level_at ? <> · {t.next_level.sb}/{t.next_level.bb} in <span data-testid="mtt-level-clock" className="inline-block w-[5ch] text-left">{mmss(t.next_level_at - now)}</span></> : ""}
        </Pill>
        {t.break_pending && <Pill testId="mtt-break-pending" tone="text-cyan-200"><Coffee className="w-3.5 h-3.5" /> Break after this hand</Pill>}
        {t.break_until && <Pill testId="mtt-break" tone="text-cyan-200"><Coffee className="w-3.5 h-3.5" /> Break · back in <span data-testid="mtt-break-clock" className="inline-block w-[5ch] text-left">{mmss(t.break_until - now)}</span></Pill>}
        <Pill testId="mtt-field"><Users className="w-3.5 h-3.5 gold-text" /> {t.remaining} of {t.registered} left · {t.tables} table{t.tables === 1 ? "" : "s"}</Pill>
        <Pill testId="mtt-pool"><Trophy className="w-3.5 h-3.5 text-amber-400" /> {fmtChips(t.prize_pool)} · {paid.length} paid</Pill>
        <Pill testId="mtt-table-no" tone="text-slate-300"><Layers className="w-3.5 h-3.5" /> Table {t.table_no} · avg {fmtChips(t.avg_stack)}</Pill>
      </div>
      {(t.rebuy_open || t.addon_open) && (
        <div className="flex items-center gap-2">
          {t.rebuy_open && <button data-testid="mtt-rebuy" disabled={busy} onClick={() => buy(C.ApiRoutes.MTT_REBUY, "Re-buy taken — back in the game")}
            className="btn-gold rounded-full px-4 py-1 text-[11px] font-display tracking-wider btn-lift">RE-BUY {fmtChips(t.rebuy_cost)} → {fmtChips(t.rebuy_chips)}</button>}
          {t.addon_open && <button data-testid="mtt-addon" disabled={busy} onClick={() => buy(C.ApiRoutes.MTT_ADDON, "Add-on taken")}
            className="btn-gold rounded-full px-4 py-1 text-[11px] font-display tracking-wider btn-lift">ADD-ON {fmtChips(t.addon_cost)} → {fmtChips(t.addon_chips)}</button>}
        </div>
      )}
      {me && (
        <Pill testId="mtt-my-place" tone="text-amber-200">You finished {ordinal(me.place)}{myPrize ? ` · ${fmtChips(myPrize)}` : " · no prize"}</Pill>
      )}
      {t.status === "FINISHED" && t.payouts?.length > 0 && (
        <Pill testId="mtt-payouts"><Trophy className="w-3.5 h-3.5 text-amber-400" /> {t.payouts.map((p) => `${ordinal(p.place)} ${p.name}${p.user_id === myUserId ? " (you)" : ""} ${fmtChips(p.amount)}`).join(" · ")}</Pill>
      )}
    </div>
  );
};
